import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Eye, 
  Trash2, 
  ShoppingCart, 
  Search, 
  Calendar, 
  DollarSign,
  Filter,
  Download,
  TrendingUp,
  Users,
  Store,
  FileText
} from 'lucide-react';
import { salesAPI } from '../services/api';
import { paymentsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { 
  ModernCard, 
  ModernButton, 
  ModernInput, 
  ModernTable,
  ModernBadge,
  ModernModal,
  ModernSearchBar,
  ModernLoadingSpinner
} from '../components/ui/ModernComponents';
import toast from 'react-hot-toast';

const Sales = () => {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const { hasAnyRole } = useAuth();

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [salePayments, setSalePayments] = useState([]);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [newPayment, setNewPayment] = useState({ amount: '', mode: 'espece', note: '' });
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [editPayment, setEditPayment] = useState({ amount: '', mode: 'espece', note: '' });

  const paymentModes = [
    { value: 'espece', label: 'Espèce' },
    { value: 'cheque', label: 'Chèque' },
    { value: 'carte_bancaire', label: 'Carte bancaire' }
  ];

  useEffect(() => {
    fetchSales();
  }, []);

  const fetchSales = async () => {
    try {
      setLoading(true);
      const response = await salesAPI.getSales();
      setSales(response.data);
    } catch (error) {
      console.error('Error fetching sales:', error);
      toast.error('Failed to load sales');
    } finally {
      setLoading(false);
    }
  };

  const filteredSales = sales.filter(sale =>
    String(sale.employee_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(sale.store_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(sale.id || '').includes(searchTerm)
  );

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  // Handle delete sale
  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette vente ?')) return;
    try {
      await salesAPI.deleteSale(id);
      toast.success('Vente supprimée');
      fetchSales();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Handle view details
  const handleViewDetails = (sale) => {
    // For now, just show an alert with sale details
    // In a real app, this would open a modal or navigate to a details page
    alert(`Détails de la vente #${sale.id}\nDate: ${formatDate(sale.sale_date)}\nEmployé: ${sale.employee_name}\nMagasin: ${sale.store_name}\nTotal: ${formatCurrency(sale.total)}`);
  };

  // Handle new sale
  const handleNewSale = () => {
    // For now, just show an alert
    // In a real app, this would open a sale creation form
    alert('Fonctionnalité de création de vente à implémenter');
  };

  const loadSalePayments = async (sale) => {
    setSelectedSale(sale);
    setPaymentLoading(true);
    try {
      const payments = (await paymentsAPI.getPaymentsForRef('sale', sale.id)).data;
      setSalePayments(payments);
    } catch { setSalePayments([]); }
    setPaymentLoading(false);
    setShowPaymentModal(true);
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    if (!selectedSale) return;
    setPaymentLoading(true);
    try {
      await paymentsAPI.createPayment({
        reference_type: 'sale',
        reference_id: selectedSale.id,
        amount: newPayment.amount,
        mode: newPayment.mode,
        note: newPayment.note
      });
      setNewPayment({ amount: '', mode: 'espece', note: '' });
      const payments = (await paymentsAPI.getPaymentsForRef('sale', selectedSale.id)).data;
      setSalePayments(payments);
    } catch {}
    setPaymentLoading(false);
  };

  const handleDeletePayment = async (id) => {
    if (!window.confirm('Supprimer ce paiement ?')) return;
    setPaymentLoading(true);
    try {
      await paymentsAPI.deletePayment(id);
      const payments = (await paymentsAPI.getPaymentsForRef('sale', selectedSale.id)).data;
      setSalePayments(payments);
    } catch {}
    setPaymentLoading(false);
  };

  const startEditPayment = (payment) => {
    setEditingPaymentId(payment.id);
    setEditPayment({ amount: payment.amount, mode: payment.mode, note: payment.note || '' });
  };
  const cancelEditPayment = () => {
    setEditingPaymentId(null);
    setEditPayment({ amount: '', mode: 'espece', note: '' });
  };
  const handleSaveEditPayment = async (e) => {
    e.preventDefault();
    if (!editingPaymentId) return;
    setPaymentLoading(true);
    try {
      await paymentsAPI.updatePayment(editingPaymentId, {
        amount: editPayment.amount,
        mode: editPayment.mode,
        note: editPayment.note
      });
      setEditingPaymentId(null);
      setEditPayment({ amount: '', mode: 'espece', note: '' });
      const payments = (await paymentsAPI.getPaymentsForRef('sale', selectedSale.id)).data;
      setSalePayments(payments);
    } catch {}
    setPaymentLoading(false);
  };
  const getPaymentStatus = (sale, payments) => {
    const paid = payments.reduce((sum, p) => sum + Number(p.amount), 0);
    if (paid === 0) return { label: 'Non payé', color: 'danger' };
    if (paid < (sale.total || 0)) return { label: 'Partiel', color: 'warning' };
    return { label: 'Payé', color: 'success' };
  };

  const handleExportPayments = async () => {
    try {
      const response = await paymentsAPI.exportPayments({ reference_type: 'sale' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'payments_sales.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      toast.error('Erreur lors de l\'export des paiements');
    }
  };

  const tableColumns = [
    { 
      key: 'id', 
      label: 'Vente ID',
      render: (value) => (
        <span className="font-mono text-sm font-semibold text-primary-600">#{value}</span>
      )
    },
    { 
      key: 'sale_date', 
      label: 'Date',
      render: (value) => (
        <div className="flex items-center">
          <Calendar className="w-4 h-4 text-neutral-400 mr-2" />
          <span className="text-sm">{formatDate(value)}</span>
        </div>
      )
    },
    { 
      key: 'employee_name', 
      label: 'Employé',
      render: (value) => (
        <div className="flex items-center">
          <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center mr-3">
            <span className="text-sm font-medium text-primary-700">
              {value?.charAt(0).toUpperCase() || '?'}
            </span>
          </div>
          <span className="font-medium text-neutral-900">{value || 'Inconnu'}</span>
        </div>
      )
    },
    { 
      key: 'store_name', 
      label: 'Magasin',
      render: (value) => (
        <div className="flex items-center">
          <Store className="w-4 h-4 text-neutral-400 mr-2" />
          <span className="text-sm">{value || '-'}</span>
        </div>
      )
    },
    { 
      key: 'total', 
      label: 'Total',
      render: (value) => (
        <span className="font-semibold text-success-600">
          {formatCurrency(value)}
        </span>
      )
    },
    { 
      key: 'actions', 
      label: 'Actions',
      render: (value, sale) => (
        <div className="flex items-center space-x-2">
          <ModernButton
            variant="ghost"
            size="sm"
            icon={<Eye className="w-4 h-4" />}
            onClick={() => handleViewDetails(sale)}
          >
            Voir
          </ModernButton>
          <ModernButton
            variant="secondary"
            size="sm"
            icon={<FileText className="w-4 h-4" />}
            onClick={() => loadSalePayments(sale)}
          >
            Paiements
          </ModernButton>
          <ModernButton
            variant="danger"
            size="sm"
            icon={<Trash2 className="w-4 h-4" />}
            onClick={() => handleDelete(sale.id)}
          >
            Supprimer
          </ModernButton>
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ModernLoadingSpinner size="lg" />
      </div>
    );
  }

  const totalRevenue = sales.reduce((sum, sale) => sum + (sale.total || 0), 0);
  const todaySales = sales.filter(sale => 
    new Date(sale.sale_date).toDateString() === new Date().toDateString()
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Ventes</h1>
          <p className="text-neutral-600">Gérez et suivez vos transactions de vente</p>
        </div>
        <div className="flex items-center space-x-3">
          <ModernSearchBar 
            placeholder="Rechercher des ventes..." 
            onSearch={setSearchTerm}
            className="w-64"
            debounce={300}
          />
          <ModernButton 
            variant="primary" 
            icon={<Plus className="w-4 h-4" />}
            onClick={handleNewSale}
          >
            Nouvelle vente
          </ModernButton>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Total Ventes</p>
              <p className="text-2xl font-bold text-neutral-900">{sales.length}</p>
            </div>
            <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center">
              <ShoppingCart className="w-6 h-6 text-success-600" />
            </div>
          </div>
        </ModernCard>
        
        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Revenus Totaux</p>
              <p className="text-2xl font-bold text-primary-600">
                {formatCurrency(totalRevenue)}
              </p>
            </div>
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </ModernCard>
        
        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Ventes Aujourd'hui</p>
              <p className="text-2xl font-bold text-warning-600">
                {todaySales.length}
              </p>
            </div>
            <div className="w-12 h-12 bg-warning-100 rounded-lg flex items-center justify-center">
              <Calendar className="w-6 h-6 text-warning-600" />
            </div>
          </div>
        </ModernCard>
        
        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Moyenne/Vente</p>
              <p className="text-2xl font-bold text-info-600">
                {sales.length > 0 ? formatCurrency(totalRevenue / sales.length) : '0 €'}
              </p>
            </div>
            <div className="w-12 h-12 bg-info-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-info-600" />
            </div>
          </div>
        </ModernCard>
      </div>

      {/* Sales Table */}
      <ModernCard className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">Ventes récentes</h3>
            <p className="text-sm text-neutral-600">{filteredSales.length} ventes trouvées</p>
          </div>
          <div className="flex items-center space-x-2">
            <ModernButton variant="ghost" size="sm" icon={<Filter className="w-4 h-4" />}>
              Filtrer
            </ModernButton>
            <ModernButton variant="ghost" size="sm" icon={<Download className="w-4 h-4" />}>
              Exporter
            </ModernButton>
            <ModernButton variant="ghost" icon={<Download className="w-4 h-4" />} onClick={handleExportPayments}>
              Export Paiements CSV
            </ModernButton>
          </div>
        </div>
        
        <ModernTable 
          columns={tableColumns} 
          data={filteredSales}
          className="w-full"
        />
      </ModernCard>

      {/* Payment Modal */}
      <ModernModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        title={`Paiements pour la vente #${selectedSale?.id}`}
      >
        <div className="space-y-4">
          <h4 className="text-lg font-semibold text-neutral-900">Détails de la vente</h4>
          <p>Date: {formatDate(selectedSale?.sale_date)}</p>
          <p>Employé: {selectedSale?.employee_name}</p>
          <p>Magasin: {selectedSale?.store_name}</p>
          <p>Total: {formatCurrency(selectedSale?.total)}</p>
          <p>Status: <ModernBadge color={getPaymentStatus(selectedSale, salePayments).color}>{getPaymentStatus(selectedSale, salePayments).label}</ModernBadge></p>

          <h4 className="text-lg font-semibold text-neutral-900">Paiements</h4>
          {paymentLoading ? (
            <ModernLoadingSpinner />
          ) : salePayments.length === 0 ? (
            <p>Aucun paiement trouvé pour cette vente.</p>
          ) : (
            <div className="space-y-3">
              {salePayments.map(payment => (
                <div key={payment.id} className="bg-gray-50 p-3 rounded-lg flex items-center justify-between">
                  <div>
                    <p className="font-medium text-neutral-900">Paiement #{payment.id}</p>
                    <p className="text-sm text-neutral-600">Montant: {formatCurrency(payment.amount)}</p>
                    <p className="text-sm text-neutral-600">Mode: {payment.mode}</p>
                    <p className="text-sm text-neutral-600">Note: {payment.note || 'Aucune note'}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <ModernButton
                      variant="ghost"
                      size="sm"
                      icon={<Eye className="w-4 h-4" />}
                      onClick={() => startEditPayment(payment)}
                    >
                      Modifier
                    </ModernButton>
                    <ModernButton
                      variant="danger"
                      size="sm"
                      icon={<Trash2 className="w-4 h-4" />}
                      onClick={() => handleDeletePayment(payment.id)}
                    >
                      Supprimer
                    </ModernButton>
                  </div>
                </div>
              ))}
            </div>
          )}

          <h4 className="text-lg font-semibold text-neutral-900">Ajouter un nouveau paiement</h4>
          <form onSubmit={handleAddPayment} className="space-y-3">
            <ModernInput
              label="Montant"
              type="number"
              value={newPayment.amount}
              onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })}
              required
            />
            <ModernInput
              label="Mode de paiement"
              type="text"
              value={newPayment.mode}
              onChange={(e) => setNewPayment({ ...newPayment, mode: e.target.value })}
              required
            />
            <ModernInput
              label="Note (optionnelle)"
              type="text"
              value={newPayment.note}
              onChange={(e) => setNewPayment({ ...newPayment, note: e.target.value })}
            />
            <ModernButton type="submit" variant="primary" disabled={paymentLoading}>
              {paymentLoading ? 'Ajout en cours...' : 'Ajouter paiement'}
            </ModernButton>
          </form>

          {editingPaymentId && (
            <div className="mt-6">
              <h4 className="text-lg font-semibold text-neutral-900">Modifier le paiement #{editingPaymentId}</h4>
              <form onSubmit={handleSaveEditPayment} className="space-y-3">
                <ModernInput
                  label="Montant"
                  type="number"
                  value={editPayment.amount}
                  onChange={(e) => setEditPayment({ ...editPayment, amount: e.target.value })}
                  required
                />
                <ModernInput
                  label="Mode de paiement"
                  type="text"
                  value={editPayment.mode}
                  onChange={(e) => setEditPayment({ ...editPayment, mode: e.target.value })}
                  required
                />
                <ModernInput
                  label="Note (optionnelle)"
                  type="text"
                  value={editPayment.note}
                  onChange={(e) => setEditPayment({ ...editPayment, note: e.target.value })}
                />
                <div className="flex justify-end space-x-2">
                  <ModernButton variant="ghost" onClick={cancelEditPayment}>
                    Annuler
                  </ModernButton>
                  <ModernButton type="submit" variant="primary" disabled={paymentLoading}>
                    {paymentLoading ? 'Enregistrement en cours...' : 'Enregistrer'}
                  </ModernButton>
                </div>
              </form>
            </div>
          )}
        </div>
      </ModernModal>
    </div>
  );
};

export default Sales; 